// Generic image component
// Will properly update path if we want to host from CDN instead of S3
import { forwardRef } from 'react';

export const getSrc = (src) => {
  if (!src || !src.includes) {
    return '';
  }
  const imageSrc = src.image_url ? src.image_url : src;
  // A rasterized mobile block is a large data URI and never an S3 URL; without this
  // every render scanned the whole string twice looking for a bucket host.
  if (imageSrc.startsWith?.('data:')) return imageSrc;
  if (imageSrc.includes('diffit-pdf-upload-dev.s3.amazonaws.com')) {
    return imageSrc.replace('diffit-pdf-upload-dev.s3.amazonaws.com', 'images-dev.diffit.me');
  } else if (imageSrc.includes('diffit-pdf-upload-prod.s3.amazonaws.com')) {
    return imageSrc.replace('diffit-pdf-upload-prod.s3.amazonaws.com', 'images.diffit.me');
  } else {
    return imageSrc;
  }
};

const Image = forwardRef(({ src, alt, ...props }, ref) => {
  // Development warnings for missing or poor alt text
  if (process.env.NODE_ENV === 'development') {
    if (alt === undefined) {
      console.warn(
        '🚨 Image component: Missing alt attribute. Please provide alt text for accessibility.',
        { src }
      );
    } else if (typeof alt === 'string' && alt.length > 0) {
      // Only check for quality issues if alt text is provided and not empty
      if (alt.length > 125) {
        console.warn(
          '💡 Image component: Alt text is quite long. Consider keeping it under 125 characters for better screen reader experience.',
          { alt: alt.substring(0, 50) + '...', src }
        );
      }
    }
    // Note: alt="" is correct for decorative images, so we don't warn about it
  }

  return (
    // eslint-disable-next-line react/forbid-elements
    <img
      ref={ref}
      src={getSrc(src)}
      alt={alt}
      {...props}
      style={{
        // Add visual indicator in development when alt is missing (undefined)
        ...(process.env.NODE_ENV === 'development' &&
          alt === undefined && {
            border: '3px solid red',
            outline: '2px solid yellow',
          }),
        ...props.style,
      }}
    />
  );
});

Image.displayName = 'Image';

export default Image;
